import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/',
      icon: 'LayoutDashboard',
      // description: 'System overview and key metrics'
    },
    {
      label: 'Cards',
      path: '/card-management',
      icon: 'CreditCard',
      // description: 'Manage NFC digital cards and assignments'
    },
    {
      label: 'Contacts',
      path: '/contacts',
      icon: 'Users',
      // description: 'Manage Contacts'
    },
    {
      label: 'Users',
      path: '/user-management',
      icon: 'Users',
      // description: 'Manage individual and team accounts'
    },
    {
      label: 'Tokens',
      path: '/token-management',
      icon: 'Users',
      // description: 'Manage individual and team accounts'
    },
    {
      label: 'Providers',
      path: '/provider-management',
      icon: 'Users',
      // description: 'Manage individual and team accounts'
    },
    {
      label: 'Billing',
      path: '/billing-management',
      icon: 'Receipt',
      // description: 'Financial management and payment processing'
    },
    {
      label: 'Reports',
      path: '/reports-analytics',
      icon: 'BarChart3',
      // description: 'Analytics and data export capabilities'
    }
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-secondary border-r border-border z-40 transition-all duration-300 ease-in-out hidden lg:block ${isCollapsed ? 'w-16' : 'w-60'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} className="text-accent-foreground" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">NFC Admin</h2>
                  <p className="text-xs text-text-muted">Card Management</p>
                </div>
              </div>
            )}
            {isCollapsed && (
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center mx-auto">
                <Icon name="Zap" size={20} className="text-accent-foreground" />
              </div>
            )}
            <Button
              variant="ghost"
              onClick={toggleSidebar}
              className="p-1.5 hover:bg-hover"
            >
              <Icon
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
                size={16}
                className="text-text-muted"
              />
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems.map((item) => (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-150 ease-out ${isActivePath(item.path)
                    ? 'bg-accent text-accent-foreground shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-hover'
                    }`}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : 'mr-3'}`}
                  />
                  {!isCollapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                </button>

                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-surface border border-border rounded text-xs text-text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                    {item.label}
                    <div className="text-text-muted text-xs mt-1">
                      {item.description}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Footer Section */}
          <div className="p-4 border-t border-border">
            {!isCollapsed && (
              <div className="text-xs text-text-muted">
                <div className="mb-2">Version 2.1.0</div>
                <div>© 2024 NFC Admin</div>
              </div>
            )}
            {isCollapsed && (
              <div className="text-center">
                <Icon name="Info" size={16} className="text-text-muted mx-auto" />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <aside
        id="mobile-sidebar"
        className="fixed top-0 left-0 h-full w-64 bg-secondary border-r border-border z-50 transform -translate-x-full transition-transform duration-300 ease-in-out lg:hidden"
      >
        <div className="flex flex-col h-full">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} className="text-accent-foreground" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">NFC Admin</h2>
                <p className="text-xs text-text-muted">Card Management</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                const sidebar = document.getElementById('mobile-sidebar');
                if (sidebar) {
                  sidebar.classList.add('-translate-x-full');
                }
              }}
              className="p-2"
            >
              <Icon name="X" size={20} className="text-text-muted" />
            </Button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  handleNavigation(item.path);
                  // Close mobile sidebar after navigation
                  const sidebar = document.getElementById('mobile-sidebar');
                  if (sidebar) {
                    sidebar.classList.add('-translate-x-full');
                  }
                }}
                className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-150 ease-out ${isActivePath(item.path)
                  ? 'bg-accent text-accent-foreground shadow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-hover'
                  }`}
              >
                <Icon name={item.icon} size={20} className="mr-3 flex-shrink-0" />
                <div className="text-left flex-1">
                  <div className="truncate">{item.label}</div>
                  <div className="text-xs text-text-muted mt-0.5 truncate">
                    {item.description}
                  </div>
                </div>
              </button>
            ))}
          </nav>

          {/* Mobile Footer */}
          <div className="p-4 border-t border-border">
            <div className="text-xs text-text-muted">
              <div className="mb-2">Version 2.1.0</div>
              <div>© 2024 NFC Admin</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden opacity-0 pointer-events-none transition-opacity duration-300"
        id="mobile-overlay"
        onClick={() => {
          const sidebar = document.getElementById('mobile-sidebar');
          if (sidebar) {
            sidebar.classList.add('-translate-x-full');
          }
        }}
      ></div>
    </>
  );
};

export default Sidebar;